import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import Header from "../components/Header";
import PhaseNav from "../components/PhaseNav";
import { Button } from "../components/ui/button";
import Phase1 from "./phases/Phase1";
import Phase2 from "./phases/Phase2";
import Phase3 from "./phases/Phase3";
import Phase4 from "./phases/Phase4";
import Phase5 from "./phases/Phase5";
import Phase6 from "./phases/Phase6";
import { ChevronLeft, ChevronRight, Users, Crown, Sparkles, Copy } from "lucide-react";

export default function Journey() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [org, setOrg] = useState(null);
  const [current, setCurrent] = useState(1);
  const [payBusy, setPayBusy] = useState(false);
  useDocumentTitle(`${t("phase")} ${current} · ${t(`p${current}_title`)}`);

  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/org/me"); setOrg(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    if (!loading && !user) nav("/auth");
  }, [loading, user, nav]);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  if (!org) return (
    <div className="min-h-screen bg-background"><Header />
      <div className="grid h-[60vh] place-items-center text-muted-foreground">{t("processing")}</div>
    </div>
  );

  const isAdmin = user.role === "superadmin" || user.role === "org_admin";
  const phase1Done = Object.values(org.phase1 || {}).some((v) => v && String(v).trim());
  const doneMap = {
    1: phase1Done,
    2: (org.touchpoints || []).length > 0,
    3: (org.feelings || []).length > 0,
    4: (org.tiles || []).filter((x) => x.selected).length === 2,
    5: !!org.core_emotion,
    6: !!(org.hero_journey && (org.hero_journey.manifesto || org.hero_journey.glory)),
  };

  const phases = [1, 2, 3, 4, 5, 6].map((n) => ({
    n, title: t(`p${n}_title`), done: doneMap[n], locked: n === 6 && !org.core_emotion,
  }));

  const upgrade = async () => {
    setPayBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: "employees", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setPayBusy(false); }
  };

  const props = { org, refresh, isAdmin };
  const inviteBase = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const inviteUrl = `${inviteBase}/auth?invite=${org.id}`;
  const PHASE_COMP = { 1: Phase1, 2: Phase2, 3: Phase3, 4: Phase4, 5: Phase5, 6: Phase6 };
  const Current = PHASE_COMP[current];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-8">
        {/* org status + upgrade */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Crown className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display font-semibold text-primary">{org.name}</p>
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${org.is_paid ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-secondary-foreground"}`}>
                {org.is_paid ? <><Users className="h-3 w-3" /> {t("paid_badge")}</> : t("free_badge")}
              </span>
            </div>
          </div>
          {!org.is_paid && isAdmin && (
            <div className="flex items-center gap-3">
              <p className="hidden max-w-sm text-xs text-muted-foreground sm:block">{t("upgrade_sub")}</p>
              <Button data-testid="stripe-upgrade-1000-button" onClick={upgrade} disabled={payBusy}
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                <Sparkles className="mr-2 h-4 w-4" /> {t("upgrade_cta")}
              </Button>
            </div>
          )}
          {org.is_paid && isAdmin && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[22rem]">
              <p className="text-xs font-semibold text-foreground">{t("invite_title")}</p>
              <p className="text-xs text-muted-foreground">{t("invite_sub")}</p>
              <div className="flex items-center gap-2">
                <input readOnly data-testid="invite-link" value={inviteUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 rounded-full border border-border bg-secondary px-3 py-1.5 text-xs text-foreground" />
                <Button data-testid="copy-invite-button" size="sm" className="rounded-full"
                  onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success(t("copied")); }}>
                  <Copy className="mr-1.5 h-4 w-4" /> {t("copy_link")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t("invite_admin_note")}</p>
            </div>
          )}
        </div>

        <div className="mb-8 rounded-2xl border border-border bg-card p-5">
          <PhaseNav phases={phases} current={current} onSelect={setCurrent} />
        </div>

        <Current {...props} />

        <div className="mt-10 flex items-center justify-between">
          <Button data-testid="phase-prev" variant="outline" className="rounded-full" disabled={current === 1}
            onClick={() => setCurrent((c) => Math.max(1, c - 1))}>
            <ChevronLeft className="mr-1 h-4 w-4" /> {t("prev")}
          </Button>
          <Button data-testid="phase-next" className="rounded-full" disabled={current === 6}
            onClick={() => setCurrent((c) => Math.min(6, c + 1))}>
            {t("next")} <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
